import React, { useEffect } from 'react';
import './App.css';
import SidebarLayout from './components/layout/SidebarLayout';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import OAuthCallback from './components/pages/OAuthCallback';
import { fetchUser } from './reducers/accountSlice';
import { useAppDispatch } from './hooks';
import LastVisitedUrlTracker from './components/nav/LastVisitedUrlTracker';
import SupportPage from './components/pages/SupportPage';
import { useToken } from './hooks/useToken';
import NoMatchEvents from './components/pages/NoMatchEvents';
import EventDeploymentsPage from './components/pages/EventDeploymentsPage';
import EventDetailPage from './components/pages/EventDetailPage';
import { useCurrentUser } from './hooks/useCurrentUser';
import { parseGuid } from './utilities/parsers';
import { getConfig } from './config/config-helper';
import NewEventSolution from './components/pages/NewEventSolution';
import EventSolutionPage from './components/pages/EventSolutionPage';
import EventInsights from './components/pages/EventInsights';
import EventAccounts from './components/pages/EventAccounts';

declare global {
  interface Window {
    Appcues: any;
  }
}

// Component to handle app-level effects and router context
function AppWithEffects() {
  const token = useToken();
  const dispatch = useAppDispatch();
  const user = useCurrentUser();
  const { dataopsShareEnvironment } = getConfig();

  useEffect(() => {
    if (token !== '') {
      dispatch(fetchUser(token)).catch((err) => {
        console.log('Fetch username error:', err);
      });
    }
    if (user.id !== '' && window.Appcues) {
      window.Appcues.identify(parseGuid(user.id), {
        email: user.emails.nodes[0]?.email,
        name: user.username,
        role: user.canPublish ? 'publisher' : user.canConsume ? 'consumer' : 'read-only',
        stage: dataopsShareEnvironment,
      });
    }
  }, [token, user.id]);

  useEffect(() => {
    const storageVersionKey = 'storageVersion';
    const storageVersion = '1';
    // Check for local storage version, if not present, clear all local storage and add version.
    // This is to ensure that local storage is cleared when the version changes.
    if (!localStorage.getItem(storageVersionKey) || localStorage.getItem(storageVersionKey) !== storageVersion) {
      localStorage.clear();
      localStorage.setItem(storageVersionKey, storageVersion);
    }
  }, []);

  return (
    <>
      <LastVisitedUrlTracker />
      <SidebarLayout />
    </>
  );
}

// Create the router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: <AppWithEffects />,
    children: [
      {
        index: true,
        element: <Navigate to="/event-deployments" replace />,
      },
      {
        path: 'event-solutions/:id',
        element: <EventSolutionPage />,
      },
      {
        path: 'new/event',
        element: <NewEventSolution />,
      },
      {
        path: 'event-deployments',
        element: <EventDeploymentsPage />,
      },
      {
        path: 'event-deployments/:eventSlug',
        element: <EventDetailPage />,
      },
      {
        path: 'event-deployments/:eventSlug/insights',
        element: <EventInsights />,
      },
      {
        path: 'event-deployments/:eventSlug/preparation',
        element: <EventAccounts />,
      },
      {
        path: 'support',
        element: <SupportPage />,
      },
      {
        path: '*',
        element: <NoMatchEvents />,
      },
    ],
  },
  {
    path: '/dataops-oauth-callback',
    element: <OAuthCallback />,
  },
]);

export default function App() {
  return (
    <div>
      <RouterProvider router={router} />
    </div>
  );
}
